import React, { useState, useMemo } from 'react';
import { Button } from '../../../components/UI';
import { IconCaretDown, IconCaretUp } from '../../../components/Icon';
import DocumentHitList from './DocumentHitList';
import type { CompanyHit } from '../types';
import styles from './CompanyCard.module.scss';
import { formatMatchCount, formatDocCount } from '../utils/formatters';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { toggleCompanyExpandedAndHydrate } from '../../../store/search/thunks';
import { Heading, Text } from '../../../components/Typography';
import { trackEvent } from '../../../services/tracking';

interface CompanyCardProps {
  company: CompanyHit;
  expanded: boolean;
}

const CompanyCard: React.FC<CompanyCardProps> = ({ company, expanded }) => {
  const dispatch = useAppDispatch();
  const [isToggling, setIsToggling] = useState(false);
  const isCompanyExpanding = useAppSelector(
    (s) => s.signalSearch.expandingCompanies[company.companyId],
  );
  const onToggle = async () => {
    if (isToggling) return;
    setIsToggling(true);
    try {
      trackEvent('CompanyCard Toggle', {
        companyId: company.companyId,
        expanded: !expanded,
      });
      await dispatch(toggleCompanyExpandedAndHydrate(company.companyId));
    } finally {
      setIsToggling(false);
    }
  };

  const maxSimilarity = useMemo(() => {
    const values: number[] = [];
    for (const doc of company.documents) {
      const sim = doc.embeddings && doc.embeddings[0] ? doc.embeddings[0].similarity : undefined;
      if (typeof sim === 'number') values.push(sim);
    }
    if (values.length === 0) return undefined;
    return Math.max(...values);
  }, [company.documents]);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <Heading level={4} className={styles.companyTitle}>
          {company.ticker} - {company.companyName}
        </Heading>
        <div className={styles.headerRight}>
          <Text element="span" weight="medium">
            {`${formatMatchCount(company.totalMatches)} · ${formatDocCount(
              company.documentMatchCount,
            )} · ${((maxSimilarity || 0) * 100).toFixed(1)}%`}
          </Text>
          <Button
            variant="ghost"
            size="small"
            onClick={onToggle}
            loading={isToggling || !!isCompanyExpanding}
            disabled={!!isCompanyExpanding}
          >
            {expanded ? <IconCaretUp /> : <IconCaretDown />}
          </Button>
        </div>
      </div>
      {expanded && (
        <DocumentHitList documents={company.documents} isCompanyExpanding={isCompanyExpanding} />
      )}
      <div className={styles.actions} />
    </div>
  );
};

export default CompanyCard;
